import React, { useState, useEffect } from 'react';
import { useSnackbar } from '../contexts/SnackbarProvider';
import GlobalError from '../components/GlobalError';
import axiosInstance from '../utils/axiosInstance';
import TableView from '../components/TableView';
import { formatDateTime, formatCurrency } from '../utils/format';
import { useNavigate } from 'react-router-dom';
import ChartOrderStatus from '../components/ChartOrderStatus';
import ChartRevenue from '../components/ChartRevenue';
import { Button, TextField, MenuItem, Stack, Typography, Grid, Link, Box, Paper } from '@mui/material';
import { BarChart, PersonAdd, ReceiptLong } from '@mui/icons-material';
import ReadMoreIcon from '@mui/icons-material/ReadMore';
const DashboardPage = ({ hasLimit }) => {
    const { showSnackbar } = useSnackbar();
    const [dataset, setDataset] = useState([]);
    const [error, setError] = useState(null);
    const [limit, setLimit] = useState(20); // 初期値は20件
    const navigate = useNavigate();
    const [orderStatusCounts, setOrderStatusCounts] = useState({
        1: 0,
        2: 0,
        3: 0,
        4: 0,
        5: 0,
        6: 0,
    });
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axiosInstance.get('/order', {
                    params: {
                        limit: limit,
                        page: 1,
                    },
                });
                setDataset(response.data.data);
            } catch (err) {
                if (process.env.NODE_ENV === 'development') { console.error(err); }
                setError(err?.response?.data?.message || '顧客情報の取得に失敗しました');
            }
        };

        fetchData();
    }, [limit]);

    useEffect(() => {
        const fetchOrderStatusCounts = async () => {
            try {
                const response = await axiosInstance.get('/order/status-counts');
                const counts = response.data;

                // counts を 1, 2, 3, 4, 5, 6 に分けて保存
                const statusCounts = {
                    1: 0,
                    2: 0,
                    3: 0,
                    4: 0,
                    5: 0,
                    6: 0,
                };

                counts.forEach((item) => {
                    if (item.order_status_id) {
                        statusCounts[item.order_status_id] = Number(item.count) || 0;
                    }
                });

                setOrderStatusCounts(statusCounts);
            } catch (err) {
                if (process.env.NODE_ENV === 'development') {
                    console.error(err);
                }
            }
        };

        fetchOrderStatusCounts();
    }, []); // 初回マウント時にデータを取得

    // 行クリック時にページ遷移を行う処理
    const handleRowClick = (item) => {
        navigate(`/orders/${item.order_id}`);
    };
    const handleNavigateOrders = (status) => {
        navigate('/orders', { state: { filterStatus: status } });
    };

    const columns = [
        {
            label: '注文日',
            accessor: 'order_date',
            format: (date) => formatDateTime(date),
            minWidth: '160px',
        },
        {
            label: '注文番号',
            accessor: 'order_number',
            render: (item) => (
                <Link
                    component="button"
                    onClick={() => handleRowClick(item)}
                >
                    {item.order_number}
                </Link>
            ),
            minWidth: '140px',
        },
        {
            label: 'ステータス',
            accessor: 'order_status_name',
            minWidth: '170px',
        },
        {
            label: '金額',
            accessor: 'total_amount',
            render: (item) => formatCurrency(item.total_amount),
            minWidth: '140px',
        },
        {
            label: '会社名＋事業所名',
            accessor: 'client_name',
            render: (item) => (<Typography>{item.client_name} {item.branch_name}</Typography>),
            width: '350px'
        },
        {
            label: '',
            render: (item) => (
                <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleRowClick(item)}
                >
                    詳細
                </Button>
            ),
            minWidth: 50,
        },
    ];

    if (error) return <GlobalError errorMessage={error} />;
    return (
        <>
            <Typography component='h1' variant='h1'>ようこそ。Tendenecへ</Typography>
            <Paper variant='section' sx={{ background: 'linear-gradient(135deg, #E2F2FF 0%, #DEDFF4 100%)' }}>
                <Stack direction="row" flexWrap='wrap' gap={1}>
                    <Button
                        variant="related-menu"
                        onClick={() => handleNavigateOrders(1)}
                    >
                        <span className='font-number'>{orderStatusCounts[1]}</span>
                        新規注文
                    </Button>
                    <Button
                        variant="related-menu"
                        onClick={() => handleNavigateOrders(3)}
                    >
                        <span className='font-number'>{orderStatusCounts[3]}</span>
                        注文処理中
                    </Button>
                    {hasLimit('max_picking_devices') && (
                        <Button
                            variant="related-menu"
                            onClick={() => navigate('/picking')}
                        >
                            <span className='font-number'>
                                {orderStatusCounts[4] + orderStatusCounts[5]}
                            </span>
                            ピッキング処理
                        </Button>
                    )}
                    <Button
                        variant="related-menu"
                        onClick={() => navigate('/orders/complete')}
                    >
                        <span className='font-number'>{orderStatusCounts[6]}</span>
                        発送待ち
                    </Button>
                    {hasLimit('max_clients') && (
                        <Button
                            variant="related-menu"
                            onClick={() => navigate('/clients', { state: { is_pending: true } })}
                        >
                            <PersonAdd />
                            新規取引申請
                        </Button>
                    )}
                    {hasLimit('data_analysis') && (
                        <Button
                            variant="related-menu"
                            onClick={() => navigate('/chart')}
                        >
                            <BarChart />
                            営業支援
                        </Button>
                    )}
                </Stack>
            </Paper>
            {hasLimit('data_analysis') && (
                <Paper variant='section'>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                        <BarChart color='primary' />
                        <Typography component='h2' variant="h3">データ分析</Typography>
                    </Stack>
                    <Grid container spacing={2} alignItems="stretch">
                        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                            <Paper
                                variant="section"
                                sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                            >
                                <ChartRevenue />
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                            <Paper
                                variant="section"
                                sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                            >
                                <ChartOrderStatus />
                            </Paper>
                        </Grid>
                    </Grid>
                    <div className="button-row">
                        <Button
                            variant="contained"
                            startIcon={<ReadMoreIcon />}
                            onClick={() => navigate('/chart')}
                        >
                            レポート一覧へ
                        </Button>
                    </div>
                </Paper>
            )}
            <Paper variant='section'>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <ReceiptLong color='primary' />
                    <Typography component='h2' variant="h3">受注の処理状況</Typography>
                    <Box sx={{ flex: 1 }} />
                    <TextField
                        select
                        size="small"
                        label="表示件数"
                        value={limit}
                        onChange={(e) => setLimit(Number(e.target.value))}
                        sx={{ width: 150 }}
                    >
                        <MenuItem value={10}>10件</MenuItem>
                        <MenuItem value={20}>20件</MenuItem>
                        <MenuItem value={50}>50件</MenuItem>
                    </TextField>
                </Stack>
                <TableView dataset={dataset} columns={columns} />
                <div className="button-row">
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => navigate('/orders')}
                        startIcon={<ReadMoreIcon />}
                        sx={{ mt: 1 }}
                    >
                        注文一覧
                    </Button>
                </div>
            </Paper>
        </>
    );
};

export default DashboardPage;
